/**
 * One deadline at a time, with its caption and its own focus action.
 *
 * OWNER ASK 2026-08-30: a Bootstrap-style carousel with captions, so a day
 * with several hard deadlines can be paged through to pick the one to work on
 * rather than stacking them all down the page.
 *
 * TWO DELIBERATE DEPARTURES FROM THE BOOTSTRAP COMPONENT, both because these
 * are DEADLINES and hiding one has a cost a marketing carousel never has:
 *
 *   1. It never auto-rotates. Bootstrap's default `ride="carousel"` moves the
 *      slide out from under you every few seconds. For a thing that can hurt
 *      you if missed, content that leaves on a timer is the wrong behaviour.
 *   2. The count stays visible ("2 of 3") and every dot is reachable, so the
 *      page never implies there is one deadline when there are three. The
 *      header already says how many; the carousel must not contradict it.
 *
 * A single deadline renders as a plain card — a carousel of one is chrome with
 * nothing to page through.
 */
import { useState } from 'react'
import { ChevronLeft, ChevronRight, Play } from 'lucide-react'

export interface DeadlineItem {
  task_id: string
  title: string
  message: string
  level?: 'error' | 'critical' | 'urgent' | 'warning' | 'info'
  consequence?: string
  days_remaining?: number
  days_overdue?: number
}

// No-guilt rule: a date that has gone by is a fact, not an accusation. It used
// to read "3d OVERDUE" in red caps (2026-09-06 audit); "overdue" is on the
// forbidden list, and shouting it at someone is the opposite of the tone.
function whenBadge(w: DeadlineItem): string | null {
  const late = w.days_overdue ?? 0
  if (late > 0) return late === 1 ? 'since yesterday' : `${late} days ago`
  if (w.days_remaining == null) return null
  if (w.days_remaining === 0) return 'today'
  if (w.days_remaining === 1) return 'tomorrow'
  return `in ${w.days_remaining} days`
}

function Slide({ w, riskLabel, onFocus, onOpen, focusLabel, openLabel }: {
  w: DeadlineItem
  riskLabel: string
  onFocus?: (taskId: string) => void
  onOpen?: (taskId: string) => void
  focusLabel: string
  openLabel: string
}) {
  const critical = w.level === 'critical' || w.level === 'error'
  const badge = whenBadge(w)
  return (
    <div className={`rounded-xl border bg-surface p-3.5 shadow-sm ${critical ? 'border-bad/50' : 'border-warn/40'}`}>
      <div className="flex items-start justify-between gap-2">
        <p className={`text-app-body font-bold ${critical ? 'text-bad' : 'text-warn'}`}>{w.title}</p>
        {badge && (
          <span className={`shrink-0 rounded px-1.5 py-0.5 text-app-micro font-semibold tabular-nums ${
            critical ? 'bg-bad/10 text-bad' : 'bg-warn/10 text-warn'}`}>
            {badge}
          </span>
        )}
      </div>

      {/* The caption. */}
      <p className="mt-1 text-app-meta text-secondary">{w.message}</p>
      {w.consequence && (
        <p className="mt-1.5 rounded-lg bg-surface-alt p-2 text-app-micro text-secondary">
          <span className="font-medium">{riskLabel}</span> {w.consequence}
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap gap-2">
        {onFocus && (
          <button
            type="button"
            onClick={() => onFocus(w.task_id)}
            className={`inline-flex min-h-touch items-center gap-1.5 rounded-lg px-3 text-app-meta font-semibold text-on-brand ${
              critical ? 'bg-bad' : 'bg-warn'}`}
          >
            <Play className="h-3.5 w-3.5 fill-current" aria-hidden="true" /> {focusLabel}
          </button>
        )}
        {onOpen && (
          <button
            type="button"
            onClick={() => onOpen(w.task_id)}
            className="inline-flex min-h-touch items-center rounded-lg border border-soft px-3 text-app-meta font-medium text-primary hover:bg-surface-alt"
          >
            {openLabel}
          </button>
        )}
      </div>
    </div>
  )
}

export function DeadlineCarousel({ items, riskLabel, focusLabel, openLabel, onFocus, onOpen }: {
  items: DeadlineItem[]
  riskLabel: string
  focusLabel: string
  openLabel: string
  onFocus?: (taskId: string) => void
  onOpen?: (taskId: string) => void
}) {
  const [i, setI] = useState(0)
  if (items.length === 0) return null

  if (items.length === 1) {
    return (
      <Slide w={items[0]} riskLabel={riskLabel} focusLabel={focusLabel} openLabel={openLabel}
             onFocus={onFocus} onOpen={onOpen} />
    )
  }

  const at = Math.min(i, items.length - 1)
  const go = (next: number) => setI((next + items.length) % items.length)

  return (
    <div
      className="space-y-2"
      role="group"
      aria-roledescription="carousel"
      aria-label={`${items.length} deadlines`}
      onKeyDown={e => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); go(at - 1) }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(at + 1) }
      }}
    >
      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => go(at - 1)}
          aria-label="Previous deadline"
          className="shrink-0 rounded-lg border border-soft px-1.5 text-muted hover:bg-surface-alt hover:text-primary"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="min-w-0 flex-1" aria-live="polite">
          <Slide w={items[at]} riskLabel={riskLabel} focusLabel={focusLabel} openLabel={openLabel}
                 onFocus={onFocus} onOpen={onOpen} />
        </div>

        <button
          type="button"
          onClick={() => go(at + 1)}
          aria-label="Next deadline"
          className="shrink-0 rounded-lg border border-soft px-1.5 text-muted hover:bg-surface-alt hover:text-primary"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Indicators + a literal count, so the page never implies there is one
          deadline when there are several. */}
      <div className="flex items-center justify-center gap-2">
        <span className="text-app-micro tabular-nums text-muted">{at + 1} of {items.length}</span>
        <span className="flex items-center gap-1">
          {items.map((w, idx) => (
            <button
              key={w.task_id}
              type="button"
              onClick={() => setI(idx)}
              aria-label={`Show ${w.title}`}
              aria-current={idx === at}
              className={`h-1.5 rounded-full transition-all ${
                idx === at ? 'w-4 bg-warn' : 'w-1.5 bg-soft hover:bg-muted'}`}
            />
          ))}
        </span>
      </div>
    </div>
  )
}

export default DeadlineCarousel
